/**
 * Office auth hook — Microsoft SSO (Office.auth / MSAL dialog) + optional email/password dialog.
 * ST_ONLY_MODE delegates to useOfficeAuthSt (PingFederate).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { config } from '../../../services/config';
import { silentAuthRequest } from '../../../auth/authConfig';
import { msalInstance } from '../../../auth/msalInstance';
import { exchangeEntraForFamlaSession } from './exchangeEntraForFamlaSession';
import {
    describeOfficeAuthErrorCode,
    openOfficeAuthDialog,
} from './openOfficeAuthDialog';
import { openOfficeEmailAuthDialog } from './openOfficeEmailAuthDialog';
import {
    clearAuthSessionMarker,
    loadUserFromFamlaToken,
    markAuthSession,
    persistFamlaToken,
    readPersistedFamlaToken,
    type OfficeAuthMethod,
    type OfficeDerivedUser,
} from './officeFamlaSession';
import { useOfficeAuthSt, type UseOfficeAuthReturn } from './useOfficeAuthSt';

export type { UseOfficeAuthReturn } from './useOfficeAuthSt';

const OFFICE_AUTH_TOKEN_KEY = 'famla.officeAuth.token';
const OFFICE_AUTH_METHOD_KEY = 'famla.officeAuth.method';
const SILENT_REFRESH_MS = 45 * 60 * 1000;
const TOKEN_EXPIRY_SKEW_SEC = 120;

type StoredToken = {
    token: string;
    exp: number;
};

function decodeJwtClaims(token: string): Record<string, any> | null {
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
        const json = atob(padded);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

function getTokenExpiryUnix(token: string): number | null {
    const claims = decodeJwtClaims(token);
    const exp = claims?.exp;
    return typeof exp === 'number' ? exp : null;
}

function isTokenUsable(token: string, nowSec = Math.floor(Date.now() / 1000)): boolean {
    const exp = getTokenExpiryUnix(token);
    if (!exp) return false;
    return exp - TOKEN_EXPIRY_SKEW_SEC > nowSec;
}

function deriveUserFromJwt(token: string): OfficeDerivedUser | null {
    const claims = decodeJwtClaims(token);
    if (!claims) return null;
    const email =
        claims.preferred_username ||
        claims.upn ||
        claims.email ||
        '';
    const name =
        claims.name ||
        [claims.given_name, claims.family_name].filter(Boolean).join(' ') ||
        (email ? String(email).split('@')[0] : '') ||
        'User';
    const azureId =
        claims.oid ||
        claims.sub ||
        email ||
        '';
    return {
        userAzureId: String(azureId),
        userEmail: String(email),
        userName: String(name),
    };
}

function deriveUserFromOfficeProfile(): OfficeDerivedUser | null {
    try {
        if (typeof Office === 'undefined') return null;
        const profile = (Office as any)?.context?.mailbox?.userProfile;
        const email = profile?.emailAddress || '';
        const name = profile?.displayName || (email ? String(email).split('@')[0] : '') || 'User';
        if (!email && !name) return null;
        return {
            userAzureId: String(email || name || ''),
            userEmail: String(email),
            userName: String(name),
        };
    } catch {
        return null;
    }
}

function hasAuthSessionMarker(): boolean {
    try {
        const raw = localStorage.getItem('famla.officeAuth.until');
        if (!raw) return false;
        const until = Number(raw);
        return Number.isFinite(until) && until > Date.now();
    } catch {
        return false;
    }
}

function persistEntraToken(token: string): void {
    try {
        const exp = getTokenExpiryUnix(token) ?? Math.floor(Date.now() / 1000) + 3600;
        const payload: StoredToken = { token, exp };
        localStorage.setItem(OFFICE_AUTH_TOKEN_KEY, JSON.stringify(payload));
        markAuthSession();
    } catch {
        markAuthSession();
    }
}

function readPersistedEntraToken(): string | null {
    try {
        const raw = localStorage.getItem(OFFICE_AUTH_TOKEN_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredToken;
        if (!parsed?.token || !isTokenUsable(parsed.token)) {
            localStorage.removeItem(OFFICE_AUTH_TOKEN_KEY);
            return null;
        }
        return parsed.token;
    } catch {
        return null;
    }
}

function clearEntraTokenStorage(): void {
    try {
        localStorage.removeItem(OFFICE_AUTH_TOKEN_KEY);
    } catch {
        // ignore
    }
}

function readStoredAuthMethod(): OfficeAuthMethod {
    try {
        const raw = localStorage.getItem(OFFICE_AUTH_METHOD_KEY);
        if (raw === 'password' || raw === 'microsoft') return raw;
    } catch {
        // ignore
    }
    return 'none';
}

function persistAuthMethod(method: OfficeAuthMethod): void {
    try {
        if (method === 'none') {
            localStorage.removeItem(OFFICE_AUTH_METHOD_KEY);
        } else {
            localStorage.setItem(OFFICE_AUTH_METHOD_KEY, method);
        }
    } catch {
        // ignore
    }
}

function logOfficeAuthError(scope: string, error: unknown): void {
    if (process.env.REACT_APP_DEBUG !== 'true') return;
    const err = error as { code?: number | string; message?: string; name?: string };
    // eslint-disable-next-line no-console
    console.warn(`[OfficeAuth] ${scope}`, {
        code: err?.code,
        name: err?.name,
        message: err?.message,
    });
}

async function waitForOfficeReady(): Promise<boolean> {
    if (typeof Office === 'undefined') return false;
    try {
        const officeAny = Office as any;
        if (typeof officeAny.onReady === 'function') {
            await officeAny.onReady();
            return true;
        }
        return true;
    } catch (error) {
        logOfficeAuthError('onReady', error);
        return false;
    }
}

function getOfficeAuthApi(): { getAccessToken: (options: Record<string, boolean>) => Promise<string> } | null {
    const officeAny = typeof Office !== 'undefined' ? (Office as any) : undefined;
    if (officeAny?.auth && typeof officeAny.auth.getAccessToken === 'function') {
        return officeAny.auth;
    }
    const runtimeAuth = (globalThis as any).OfficeRuntime?.auth;
    if (runtimeAuth && typeof runtimeAuth.getAccessToken === 'function') {
        return runtimeAuth;
    }
    return null;
}

async function tryOfficeMsalSilent(): Promise<string | null> {
    try {
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) return null;
        const result = await msalInstance.acquireTokenSilent({
            ...silentAuthRequest,
            account: accounts[0],
        });
        return result.accessToken || null;
    } catch (error) {
        if (!(error instanceof InteractionRequiredAuthError)) {
            logOfficeAuthError('msal.acquireTokenSilent', error);
        }
        return null;
    }
}

function emptyAuthReturn(): UseOfficeAuthReturn {
    return {
        isAuthenticated: false,
        userAzureId: '',
        userEmail: '',
        userName: 'User',
        accessToken: null,
        signIn: async () => false,
        signInWithEmail: async () => false,
        signInWithMicrosoft: async () => false,
        authMethod: 'none',
        hasEntraToken: false,
        isLoading: false,
        authError: null,
    };
}

function useOfficeAuthMicrosoft({ enabled }: { enabled: boolean }): UseOfficeAuthReturn {
    const emailPasswordEnabled = config.app.officeEmailPasswordAuth;

    const [accessToken, setAccessToken] = useState<string | null>(() => {
        if (!enabled) return null;
        if (emailPasswordEnabled) {
            const famla = readPersistedFamlaToken();
            if (famla) return famla;
        }
        return readPersistedEntraToken();
    });
    const [authMethod, setAuthMethod] = useState<OfficeAuthMethod>(() =>
        enabled ? readStoredAuthMethod() : 'none'
    );
    const [entraToken, setEntraToken] = useState<string | null>(() =>
        enabled ? readPersistedEntraToken() : null
    );
    const [isLoading, setIsLoading] = useState(enabled);
    const [authError, setAuthError] = useState<string | null>(null);
    const [derivedUser, setDerivedUser] = useState<OfficeDerivedUser>(() => {
        if (!enabled) {
            return { userAzureId: '', userEmail: '', userName: 'User' };
        }
        const famla = emailPasswordEnabled ? readPersistedFamlaToken() : null;
        if (famla) {
            return deriveUserFromJwt(famla) || {
                userAzureId: '',
                userEmail: '',
                userName: 'User',
            };
        }
        const cached = readPersistedEntraToken();
        return (
            (cached && deriveUserFromJwt(cached)) || {
                userAzureId: '',
                userEmail: '',
                userName: 'User',
            }
        );
    });
    const refreshingRef = useRef(false);
    const accessTokenRef = useRef<string | null>(accessToken);
    accessTokenRef.current = accessToken;

    const isAuthenticated = Boolean(accessToken);
    const hasEntraToken = Boolean(entraToken && isTokenUsable(entraToken));

    const applyFamlaToken = useCallback(async (token: string, refreshToken?: string) => {
        setAccessToken(token);
        setAuthMethod('password');
        persistAuthMethod('password');
        setEntraToken(null);
        clearEntraTokenStorage();
        setAuthError(null);
        persistFamlaToken(token, refreshToken);
        markAuthSession();
        const user = await loadUserFromFamlaToken(token);
        if (user) {
            setDerivedUser(user);
        } else {
            const jwtUser = deriveUserFromJwt(token);
            if (jwtUser) setDerivedUser(jwtUser);
        }
    }, []);

    const applyEntraToken = useCallback((token: string) => {
        setAccessToken(token);
        setEntraToken(token);
        setAuthMethod('microsoft');
        persistAuthMethod('microsoft');
        setAuthError(null);
        persistEntraToken(token);
        const jwtUser = deriveUserFromJwt(token);
        if (jwtUser) {
            setDerivedUser(jwtUser);
        } else {
            const officeProfileUser = deriveUserFromOfficeProfile();
            if (officeProfileUser) {
                setDerivedUser((prev) => ({
                    userAzureId: prev.userAzureId || officeProfileUser.userAzureId,
                    userEmail: prev.userEmail || officeProfileUser.userEmail,
                    userName: prev.userName || officeProfileUser.userName,
                }));
            }
        }

        void exchangeEntraForFamlaSession(token).catch((error) => {
            logOfficeAuthError('exchangeEntraForFamlaSession', error);
        });
    }, []);

    const tryOfficeToken = useCallback(
        async (allowPrompts: boolean): Promise<string | null> => {
            const authApi = getOfficeAuthApi();
            if (!authApi) return null;
            try {
                const token: string = await authApi.getAccessToken({
                    allowSignInPrompt: allowPrompts,
                    allowConsentPrompt: allowPrompts,
                });
                return token || null;
            } catch (error) {
                logOfficeAuthError(allowPrompts ? 'getAccessToken(interactive)' : 'getAccessToken(silent)', error);
                return null;
            }
        },
        []
    );

    const acquireMicrosoftSilentCore = useCallback(async (): Promise<boolean> => {
        await waitForOfficeReady();

        const cached = readPersistedEntraToken();
        if (cached) {
            applyEntraToken(cached);
        }

        let token = await tryOfficeToken(false);

        if (!token && hasAuthSessionMarker()) {
            token = await tryOfficeToken(true);
        }

        if (token) {
            applyEntraToken(token);
            return true;
        }

        const msalToken = await tryOfficeMsalSilent();
        if (msalToken) {
            applyEntraToken(msalToken);
            return true;
        }

        return Boolean(cached);
    }, [applyEntraToken, tryOfficeToken]);

    const restoreSession = useCallback(async (showLoading: boolean): Promise<boolean> => {
        if (!enabled) return false;
        if (refreshingRef.current) return Boolean(accessTokenRef.current);
        refreshingRef.current = true;
        if (showLoading) setIsLoading(true);

        try {
            if (emailPasswordEnabled) {
                const famla = readPersistedFamlaToken();
                if (famla) {
                    const user = await loadUserFromFamlaToken(famla);
                    if (user) {
                        setAccessToken(famla);
                        setDerivedUser(user);
                        setAuthMethod('password');
                        persistAuthMethod('password');
                        setEntraToken(null);
                        markAuthSession();
                        return true;
                    }
                    clearAuthSessionMarker();
                }
            }

            return await acquireMicrosoftSilentCore();
        } finally {
            refreshingRef.current = false;
            if (showLoading) setIsLoading(false);
        }
    }, [acquireMicrosoftSilentCore, emailPasswordEnabled, enabled]);

    useEffect(() => {
        if (!enabled) return;
        void restoreSession(true);
    }, [enabled, restoreSession]);

    useEffect(() => {
        if (!enabled) return;

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                void restoreSession(false);
            }
        };
        const onFocus = () => {
            void restoreSession(false);
        };

        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        const intervalId = window.setInterval(() => {
            void restoreSession(false);
        }, SILENT_REFRESH_MS);

        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onFocus);
            window.clearInterval(intervalId);
        };
    }, [enabled, restoreSession]);

    const signInWithMicrosoft = useCallback(async (): Promise<boolean> => {
        if (!enabled) return false;
        setIsLoading(true);
        setAuthError(null);
        try {
            await waitForOfficeReady();

            let token = await tryOfficeToken(true);
            if (token) {
                applyEntraToken(token);
                setIsLoading(false);
                return true;
            }

            token = await tryOfficeMsalSilent();
            if (token) {
                applyEntraToken(token);
                setIsLoading(false);
                return true;
            }

            try {
                const dialogToken = await openOfficeAuthDialog();
                if (dialogToken) {
                    applyEntraToken(dialogToken);
                    setIsLoading(false);
                    return true;
                }
            } catch (dialogError) {
                logOfficeAuthError('displayDialogAsync', dialogError);
                const message =
                    dialogError instanceof Error
                        ? dialogError.message
                        : describeOfficeAuthErrorCode(undefined);
                setAuthError(message);
            }

            setIsLoading(false);
            return false;
        } catch (error) {
            logOfficeAuthError('signInWithMicrosoft', error);
            const err = error as { code?: number | string; message?: string };
            setAuthError(
                err.message || describeOfficeAuthErrorCode(err.code)
            );
            setAccessToken(null);
            setEntraToken(null);
            setAuthMethod('none');
            persistAuthMethod('none');
            clearEntraTokenStorage();
            clearAuthSessionMarker();
            setIsLoading(false);
            return false;
        }
    }, [applyEntraToken, enabled, tryOfficeToken]);

    const signInWithEmail = useCallback(async (): Promise<boolean> => {
        if (!enabled || !emailPasswordEnabled) return false;
        setIsLoading(true);
        setAuthError(null);
        try {
            await waitForOfficeReady();
            const { accessToken: token, refreshToken } = await openOfficeEmailAuthDialog();
            await applyFamlaToken(token, refreshToken);
            setIsLoading(false);
            return true;
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Email sign-in failed';
            setAuthError(message);
            setIsLoading(false);
            return false;
        }
    }, [applyFamlaToken, emailPasswordEnabled, enabled]);

    const signIn = useCallback(async (): Promise<boolean> => {
        if (emailPasswordEnabled) {
            return signInWithEmail();
        }
        return signInWithMicrosoft();
    }, [emailPasswordEnabled, signInWithEmail, signInWithMicrosoft]);

    const effectiveUser: OfficeDerivedUser =
        derivedUser.userEmail || derivedUser.userAzureId
            ? derivedUser
            : { userAzureId: '', userEmail: '', userName: 'User' };

    if (!enabled) {
        return emptyAuthReturn();
    }

    return {
        isAuthenticated,
        userAzureId: effectiveUser.userAzureId,
        userEmail: effectiveUser.userEmail,
        userName: effectiveUser.userName,
        accessToken,
        signIn,
        signInWithEmail,
        signInWithMicrosoft,
        authMethod,
        hasEntraToken,
        isLoading,
        authError,
    };
}

export function useOfficeAuth(): UseOfficeAuthReturn {
    const stOnlyMode = config.app.stOnlyMode;
    const stAuth = useOfficeAuthSt({ enabled: stOnlyMode });
    const microsoftAuth = useOfficeAuthMicrosoft({ enabled: !stOnlyMode });
    return stOnlyMode ? stAuth : microsoftAuth;
}

export default useOfficeAuth;
